import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import { CountryService } from "../../../services/user/country.services";
import { StateService } from "../../../services/user/state.services";
import { CustomerService } from "../../../services/user/customer.services";
import { Editor } from "@tinymce/tinymce-react";
import Select from 'react-select';
import striptags from "striptags";

const AddCustomerComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const customerId = location.state?.customerId || 0;
  const leadData = location.state?.leadData;
  const isConvertingLead = !!leadData;
  const isEditMode = !!customerId;
  const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);

  const formik = useFormik({
    initialValues: {
      companyName: "",
      GSTNo: "",
      email: "",
      website: "",
      phoneNumber: "",
      contactName: "",
      designation: "",
      mobileNo: "",
      billingAddressLine1: "",
      billingCity: "",
      billingStateId: 0,
      billingPincode: "",
      billingCountryId: 0,
      billingCurrencyId: 0,
      shippingAddressLine1: "",
      shippingCity: "",
      shippingStateId: 0,
      shippingPincode: "",
      shippingCountryId: 0,
      shippingCurrencyId: 0,
      customerDescription: "",
      isActive: true,
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Please enter a valid company name"),
      phoneNumber: Yup.string().required("Please enter a valid PhoneNumber"),
      //pinCode: Yup.string().required("Please enter a valid PinCode"),
    }),
    onSubmit: async (values) => {
      try {
        let cleanedcustomerDescription = striptags(values.customerDescription);
        setLoading(true);
        const data = {
          ...values,
          customerDescription: cleanedcustomerDescription,
        };
        if (isConvertingLead) {
          const response = await CustomerService.convertLeadToCustomer(values);
          navigate("/customer", { state: { customerId: response.customerId } });
        }
        else if (isEditMode) {
          const response = await CustomerService.updateCustomerById(customerId, data);
          if (response) {
            navigate("/customer");
          }
        }
        else {
          await CustomerService.addNewCustomer(values);
          navigate("/customer");
        }
      } catch (error) {
        handleNotify(
          "An error occurred while saving the customer.",
          TOASTER_TYPE.ERROR,
          TOASTER_POSITION.TOP_RIGHT
        );
      } finally {
        setLoading(false);
      }
    },

  });

  //values converting the lead to a customer
  useEffect(() => {
    if (isConvertingLead && leadData) {
      formik.setValues({
        companyName: leadData?.companyName || "",
        GSTNo: leadData.GSTNo || "",
        email: leadData?.email || "",
        website: leadData?.website || "",
        phoneNumber: leadData?.phoneNumber || "",
        contactName: leadData?.contactName || "",
        designation: leadData?.position || "",
        mobileNo: leadData.mobileNo || "",
        shippingAddressLine1: leadData?.addressLine1,
        shippingCity: leadData?.city,
        shippingStateId: leadData?.stateId,
        shippingPincode: leadData?.pinCode,
        shippingCountryId: leadData?.countryId,
        customerDescription: leadData?.leadDescription,
        isActive: leadData?.isActive ?? true,
      });
    }
  }, []);


  useEffect(() => {
    if (isEditMode) {
      const fetchCustomerDetail = async () => {
        try {
          const data = await CustomerService.getCustomerById(customerId);
          formik.setValues({
            companyName: data?.companyName,
            GSTNo: data?.GSTNo || "",
            email: data?.email,
            website: data?.website,
            phoneNumber: data?.phoneNumber,
            contactName: data?.contactName,
            designation: data?.designation || "",
            mobileNo: data?.mobileNo || "",

            billingAddressLine1: data?.billingAddress?.addressLine1,
            billingCity: data?.billingAddress?.city,
            billingStateId: data?.billingAddress?.stateId || 0,
            billingPincode: data?.billingAddress?.pinCode || 0,
            billingCountryId: data?.billingAddress?.countryId || 0,
            billingCurrencyId: data?.billingAddress?.currencyId || 0,

            shippingAddressLine1: data?.shippingAddress?.addressLine1 || "",
            shippingCity: data?.shippingAddress?.city || "",
            shippingStateId: data?.shippingAddress?.stateId || 0,
            shippingPincode: data?.shippingAddress?.pinCode || 0,
            shippingCountryId: data?.shippingAddress?.countryId || 0,
            shippingCurrencyId: data?.shippingAddress?.currencyId || 0,

            customerDescription: data?.customerDescription || "",
            isActive: data?.isActive ?? true,
          });
        } catch (error) {
          handleNotify(
            'Error fetching customer details', error,
            TOASTER_TYPE.ERROR,
            TOASTER_POSITION.TOP_RIGHT
          );
        }
      };
      fetchCustomerDetail();
    }
  }, []);

  const fetchStates = async () => {
    try {
      const response = await StateService.getAllStates(selectedStore);
      if (response) {
        const sortedState = response.sort((a, b) => a.stateId - b.stateId);
        const stateOptions = sortedState.map(state => ({
          value: state.stateId,
          label: state.stateName,
        }));
        if (!formik.values.billingStateId && stateOptions.length > 0) {
          formik.setFieldValue("billingStateId", stateOptions[0].value);
        }
        if (!formik.values.shippingStateId && stateOptions.length > 0) {
          formik.setFieldValue("shippingStateId", stateOptions[0].value);
        }
        //formik.setFieldValue('billingStateId', stateOptions[0]?.stateId)
        setStates(stateOptions);
      }
      else {
        setStates([]);
      }
    } catch (error) {
      handleNotify(
        "Error fetching states:", error,
        TOASTER_TYPE.ERROR,
        TOASTER_POSITION.TOP_RIGHT
      );
    }
  };

  const fetchCountry = async () => {
    try {
      const response = await CountryService.getAllCountry(selectedStore);
      if (response) {
        const sortedCountry = response.sort((a, b) => a.countryId - b.countryId);
        const countryIdOptions = sortedCountry.map(country => ({
          value: country.countryId, //change as id for send as id 
          label: country.countryName,
        }));
        if (!formik.values.billingCountryId && countryIdOptions.length > 0) {
          formik.setFieldValue("billingCountryId", countryIdOptions[0].value);
        }
        if (!formik.values.shippingCountryId && countryIdOptions.length > 0) {
          formik.setFieldValue("shippingCountryId", countryIdOptions[0].value);
        }
        setCountries(countryIdOptions);
      }
      else {
        setCountries([]);
      }

    } catch (error) {
      handleNotify(
        "Error fetching countries:", error,
        TOASTER_TYPE.ERROR,
        TOASTER_POSITION.TOP_RIGHT
      );
    }
  };
  useEffect(() => {
    fetchStates();
    fetchCountry();
  }, [selectedStore]);

  const handleBackClick = () => {
    navigate("/customer");
  };

  // console.log(formik.errors)

  return (
    <div className="page-wrapper m-0">
      <div className="page-content">
        {/* Breadcrumb Navigation */}
        <div className="page-breadcrumb d-flex align-items-center mb-3">
          <div
            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
            onClick={handleBackClick}
          >
            Customers</div>
          <div className="ps-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 p-0">
                <li className="breadcrumb-item active" aria-current="page">
                  {isConvertingLead ? "Convert Lead to New Customer" : isEditMode ? "Edit Customer" : "New Customer"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3"
              onClick={handleBackClick}
            >
              Back
            </button>
          </div>
        </div>

        {/* Main Card for Form with Border (No Shadow) */}
        <div className="card radius-10 border-top-0 border-3 border-primary">
          <div className="card-body p-5">
            <form onSubmit={formik.handleSubmit}>
              {/* Company Info */}
              <div className="card mb-4 p-4 border-top-0 border-3 border-primary">
                <h5 className="form-label d-flex ms-1 mt-3 text-primary fw-bold">
                  Company Info
                </h5>
                <hr className="radius-10 border-top-0 border-4 border-primary" />

                <div className="card-body">
                  <div className="row mb-4">
                    {/* Company Name */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="companyName" className="form-label d-flex">
                        Company Name <span className="text-danger">*</span>:
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        placeholder="Enter Company Name"
                        className={`form-control ${formik.touched.companyName && formik.errors.companyName ? "is-invalid" : ""}`}
                        value={formik.values.companyName}
                        // {...formik.getFieldProps('companyName')}
                        onChange={formik.handleChange}
                      />
                      {formik.touched.companyName && formik.errors.companyName && (
                        <div className="invalid-feedback d-flex">{formik.errors.companyName}</div>
                      )}
                    </div>
                    {/* GST No */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="GSTNo" className="form-label d-flex">
                        GST No:
                      </label>
                      <input
                        type="text"
                        id="GSTNo"
                        name="GSTNo"
                        placeholder="Enter GST Registration Number"
                        className="form-control"
                        value={formik.values.GSTNo}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                  <div className="row mb-4">
                    {/* Email */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="email" className="form-label d-flex">
                        Email:
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        placeholder="example@gmail.com"
                        className="form-control"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                      />
                    </div>
                    {/* Website */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="website" className="form-label d-flex">
                        Website:
                      </label>
                      <input
                        type="text"
                        id="website"
                        name="website"
                        placeholder="Enter Website URL"
                        className="form-control"
                        value={formik.values.website}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                  <div className="row mb-4">
                    {/* Phone Number */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="phoneNumber" className="form-label d-flex">
                        Phone Number <span className="text-danger">*</span>:
                      </label>
                      <input
                        type="text"
                        id="phoneNumber"
                        name="phoneNumber"
                        placeholder="Enter Phone Number"
                        className={`form-control ${formik.touched.phoneNumber && formik.errors.phoneNumber ? "is-invalid" : ""}`}
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                      />
                      {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                        <div className="invalid-feedback d-flex">{formik.errors.phoneNumber}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Person */}
              <div className="card mb-4 p-4 border-top-0 border-3 border-primary">
                <h5 className="form-label d-flex ms-1 mt-3 text-primary fw-bold">
                  Contact Person
                </h5>
                <hr className="radius-10 border-top-0 border-4 border-primary" />

                <div className="card-body">
                  <div className="row mb-4">
                    {/* Name */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="contactName" className="form-label d-flex">
                        Name:
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        placeholder="Enter Name"
                        className="form-control"
                        value={formik.values.contactName}
                        onChange={formik.handleChange}
                      />
                    </div>
                    {/* Designation */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="designation" className="form-label d-flex">
                        Designation:
                      </label>
                      <input
                        type="text"
                        id="designation"
                        name="designation"
                        placeholder="Enter Designation"
                        className="form-control"
                        value={formik.values.designation}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                  <div className="row mb-4">
                    {/* Mobile No */}
                    <div className="col-md-6 col-12">
                      <label htmlFor="mobileNo" className="form-label d-flex">
                        Mobile No:
                      </label>
                      <input
                        type="text"
                        id="mobileNo"
                        name="mobileNo"
                        placeholder="Enter Mobile Number"
                        className="form-control"
                        value={formik.values.mobileNo}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing  Address */}
              <div className="row mb-4 ">
                {/* Billing Address */}
                <div className="col-md-6 col-12">
                  <div className="card p-4 border-top-0 border-3 border-primary">
                    <h5 className="form-label ms-1 mt-3 text-primary fw-bold d-flex">Billing Address</h5>
                    <hr className="radius-10 border-top-0 border-4 border-primary" />
                    <div className="card-body">
                      <div className="row mb-4">
                        {/* Address Line 1 */}
                        <div className="col-12">
                          <label htmlFor="billingAddressLine1" className="form-label d-flex">
                            Address:
                          </label>
                          <textarea
                            id="billingAddressLine1"
                            name="billingAddressLine1"
                            placeholder="Enter Billing Address"
                            className="form-control"
                            value={formik.values.billingAddressLine1}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row mb-4">
                        {/* Country */}
                        <div className="col-12">
                          <label htmlFor="billingCountryId" className="form-label d-flex">
                            Country:
                          </label>
                          <div className="text-start">
                            <Select
                              id="billingCountryId"
                              name="billingCountryId"
                              options={countries}
                              value={countries.find(option => option.value === formik.values.billingCountryId)}
                              onChange={(selectedOption) => formik.setFieldValue('billingCountryId', selectedOption.value)}
                              menuPortalTarget={document.body}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Other Address Fields */}
                      <div className="row mb-4">
                        {/* State, City, Pincode */}
                        <div className="col-12">
                          <label htmlFor="billingStateId" className="form-label d-flex">State:</label>
                          <div className="text-start">
                            <Select
                              id="billingStateId"
                              name="billingStateId"
                              options={states}
                              value={states.find(option => option.value === formik.values.billingStateId)}
                              onChange={(selectedOption) => formik.setFieldValue('billingStateId', selectedOption.value)}
                              menuPortalTarget={document.body}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="billingCity" className="form-label d-flex">
                            City:
                          </label>
                          <input
                            type="text"
                            id="billingCity"
                            name="billingCity"
                            placeholder="Enter Billing City Name"
                            className="form-control"
                            value={formik.values.billingCity}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="billingPincode" className="form-label d-flex">
                            Pincode<span className="text-danger">*</span>:
                          </label>
                          <input
                            type="text"
                            id="billingPincode"
                            name="billingPincode"
                            placeholder="Enter Billing Pincode"
                            className={`form-control ${formik.touched.billingPincode && formik.errors.billingPincode ? "is-invalid" : ""}`}
                            value={formik.values.billingPincode}
                            onChange={formik.handleChange}
                          />
                          {formik.touched.billingPincode && formik.errors.billingPincode && (
                            <div className="invalid-feedback d-flex">{formik.errors.billingPincode}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Shipping Address */}
                <div className="col-md-6 col-12">
                  <div className="card p-4 border-top-0 border-3 border-primary">
                    <div className="d-flex justify-content-between align-items-center ms-1 mt-3">
                      <h5 className="form-label text-primary fw-bold">Shipping Address</h5>
                      <button type="button" className="btn btn-secondary me-2">
                        Copy Address
                      </button>
                    </div>
                    <hr className="radius-10 border-top-0 border-4 border-primary" />
                    <div className="card-body">
                      <div className="row mb-4">
                        {/* Address */}
                        <div className="col-12">
                          <label htmlFor="shippingAddressLine1" className="form-label d-flex">
                            Address:
                          </label>
                          <textarea
                            id="shippingAddressLine1"
                            name="shippingAddressLine1"
                            placeholder="Enter Shipping Address"
                            className="form-control"
                            value={formik.values.shippingAddressLine1}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                      {/* Country, State, City, Pincode */}
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="shippingCountryId" className="form-label d-flex">
                            Country:
                          </label>
                          <div className="text-start">
                            <Select
                              id="shippingCountryId"
                              name="shippingCountryId"
                              options={countries}
                              value={countries.find(option => option.value === formik.values.shippingCountryId)}
                              onChange={(selectedOption) => formik.setFieldValue('shippingCountryId', selectedOption.value)}
                              menuPortalTarget={document.body}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="shippingStateId" className="form-label d-flex">
                            State:
                          </label>
                          <div className="text-start">
                            <Select
                              id="shippingStateId"
                              name="shippingStateId"
                              options={states}
                              value={states.find(option => option.value === formik.values.shippingStateId)}
                              onChange={(selectedOption) => formik.setFieldValue('shippingStateId', selectedOption.value)}
                              menuPortalTarget={document.body}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="shippingCity" className="form-label d-flex">
                            City:
                          </label>
                          <input
                            type="text"
                            id="shippingCity"
                            name="shippingCity"
                            placeholder="Enter Shipping City Name"
                            className="form-control"
                            value={formik.values.shippingCity}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                      <div className="row mb-4">
                        <div className="col-12">
                          <label htmlFor="shippingPincode" className="form-label d-flex">Pincode:</label>
                          <input
                            type="number"
                            id="shippingPincode"
                            name="shippingPincode"
                            className="form-control"
                            placeholder="Enter Shipping Pincode"
                            value={formik.values.shippingPincode}
                            onChange={formik.handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-2 mb-5">
                <div className="col-12">
                  <label htmlFor="customerDescription" className="form-label d-flex">
                    Description:
                  </label>
                  <Editor
                    value={formik.values.customerDescription}
                    tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                    init={{
                      height: 300,
                      menubar: true,
                      plugins: [
                        "advlist autolink lists link image charmap print preview anchor",
                        "searchreplace visualblocks code fullscreen",
                        "insertdatetime media table paste code help wordcount",
                      ],
                      toolbar:
                        "undo redo | formatselect | bold italic backcolor | \
                                                                     alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                                     link image",
                    }}
                    onEditorChange={(content) =>
                      formik.setFieldValue("customerDescription", content)
                    }
                  />
                </div>
              </div>

              {/* Status Radio Buttons */}
              <div className="d-flex align-items-center mb-4">
                <label className="form-label me-3">Status:</label>
                <div className="form-check form-check-inline me-3">
                  <input
                    type="radio"
                    id="isActive"
                    name="isActive"
                    className="form-check-input"
                    value={true}
                    checked={formik.values.isActive === true}
                    onChange={() => formik.setFieldValue("isActive", true)}
                  />
                  <label className="form-check-label" htmlFor="active">Active</label>
                </div>
                <div className="form-check form-check-inline">
                  <input
                    type="radio"
                    id="inactive"
                    name="isActive"
                    className="form-check-input"
                    value={false}
                    checked={formik.values.isActive === false}
                    onChange={() => formik.setFieldValue("isActive", false)}
                  />
                  <label className="form-check-label" htmlFor="inactive">Inactive</label>
                </div>
              </div>

              {/* Action Buttons */}
              <hr />
              <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3"
                  onClick={handleBackClick}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 rounded-3"
                >
                  {isEditMode ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
export default AddCustomerComponent;
